  // ReportListMainContent.js
import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import {
  FaCopy, FaUndo, FaBrain, FaPrint, FaEnvelope, FaChevronRight,
  FaFileWord, FaEllipsisV,
  FaSpinner, FaList, FaArchive, FaExclamationTriangle, FaTimes, FaBuilding, FaCamera, FaFileCsv, FaChartLine, FaDownload, FaPaste, FaPrescriptionBottle, FaEraser, FaFolder, FaMapMarkerAlt
} from 'react-icons/fa';
import SectionsOverlay from '../components/overlays/SectionsOverlay';
import Button from '../components/buttons/Button';
import MenuButton from '../components/buttons/MenuButton';
import HoverTooltip from '../components/tooltips/HoverTooltip';
import StructuredDataManager, { useHasStructuredData } from './StructuredDataManager';
import { isCapacitor, isElectron, isAndroid } from '../utils/platform';
import { useAuth } from '../context/AuthContext';
import { createLogger } from '../services/Debug';
import useNotification from '../components/Notifications/useNotifications';
import Notification from '../components/Notifications/Notification';

export default function ReportListMainContent({
  selectedReport,
  mdReport,
  onMdReportChange,
  isInsertMode,
  sections,
  onSendToServer,
  isSending,
  onArchiveReport,
  onDeleteReport,
  onContextMenu,
  onCopy,
  onInsertion,
  onSendEmail,
  onToggleExportMenu,
  onToggleTextOptions,
  showTextOptions,
  showExportMenu,
  showTemplateOverlay,
  onToggleReportTypeOverlay,
  onToggleAnnexPanel,
  onCleanFormatting,
  onToggleDestinatairePanel,
  onExportButtonClick,
  onExportOpenClick,
  onExportDownloadClick,
  onSetDefaultFolder,
  defaultExportDir,
  onToggleSortByDate,
  sortMonthDay,
  onExportSectionsCSV,
  onPrint,
  onUndo,
  onBullseyeChange,
  onCopySection,
  copiedSections = [],
  caretPos,
  onUpdateCaretPosition,
  isSidebarVisible,
  isSmallScreen,
  onSetSidebarVisible,
  onSidebarIndicatorClick,
  textareaRef,
  openFullScreenEditor,
  fullScreenEditorProps,
  isFullScreenEnabled,
  isEnterpriseAdmin,
  onCreateEnterpriseExample,
  enterpriseExampleLoading,
  isSystemAdmin,
  onCreateAdminDefaultExample,
  adminDefaultExampleLoading,
  hasUncertaintyMarkers = false,
  uncertaintyPreview = '',
  showSchema,
  setShowSchema,
  onGeneratePrescription,
  onCleanUncertaintyMarkers
}) {
  const log = createLogger('ReportListMainContent');
  const { profile } = useAuth();
  const { notification, showNotification, hideNotification } = useNotification();
  const isGoldOrEnterprise = profile?.isGoldOrEnterprise;
  const isSilverOrGoldOrEnterprise = profile?.isSilverOrGoldOrEnterprise;
  const silverGatingMsg = 'Réservé aux abonnements Silver et plus';
  const goldGatingMsg = 'Réservé aux abonnements Gold et Entreprise';

  const [internalShowSchema, setInternalShowSchema] = useState(false);
  const actualShowSchema = showSchema !== undefined ? showSchema : internalShowSchema;
  const actualSetShowSchema = setShowSchema || setInternalShowSchema;
  const hasStructured = useHasStructuredData(selectedReport?.structuredData);
  const structuredDataRef = useRef(null);
  const shortDefaultExportDir = useMemo(() => {
    try {
      const parts = String(defaultExportDir || '').split(/[/\\]+/).filter(Boolean);
      if (parts.length >= 2) return `${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
      return parts.join('/') || '';
    } catch (_) {
      return '';
    }
  }, [defaultExportDir]);
  
  // Word count calculation
  const wordCount = mdReport ? mdReport.trim().split(/\s+/).filter(word => word.length > 0).length : 0;
  const charCount = mdReport ? mdReport.length : 0;
  
  // Auto-resize textarea
  const autoResize = useCallback(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      const newHeight = Math.max(isCapacitor ? 300 : 400, Math.min(800, textarea.scrollHeight));
      textarea.style.height = `${newHeight}px`;
    }
  }, []);
  
  // Auto-resize on content change
  useEffect(() => {
    autoResize();
  }, [mdReport, autoResize]);

  // Reset schema view when report changes or has no structured data
  useEffect(() => {
    if (!selectedReport || !hasStructured) {
      actualSetShowSchema(false);
    }
  }, [selectedReport, hasStructured]);
  
  // Handle textarea focus for fullscreen editor on Capacitor
  const handleTextareaFocus = useCallback((e) => {
    if (isFullScreenEnabled && isCapacitor && selectedReport) {
      e.preventDefault();
      e.target.blur(); // Prevent native keyboard
      openFullScreenEditor(mdReport);
    }
  }, [isFullScreenEnabled, selectedReport, mdReport, openFullScreenEditor]);

  const handleTextareaClick = useCallback((e) => {
    if (isFullScreenEnabled && isCapacitor && selectedReport) {
      e.preventDefault();
      handleTextareaFocus(e);
    }
  }, [isFullScreenEnabled, selectedReport, handleTextareaFocus]);

  // Schema export handlers
  const handleCopySchema = useCallback(async () => {
    if (structuredDataRef.current?.copySchema) {
      const success = await structuredDataRef.current.copySchema();
      if (success) {
      showNotification('Schéma copié dans le presse-papiers', 'success');
      }
    }
  }, []);

  const handleDownloadSchema = useCallback(async () => {
    if (structuredDataRef.current?.downloadSchema) {
      const success = await structuredDataRef.current.downloadSchema();
      if (success) {
        // You could add a notification here if needed
        log.debug('Schema downloaded successfully');
      }
    }
  }, []);

  return (
    <div className={`${isCapacitor ? 'mobile-main-content' : 'main-content'} ${isSidebarVisible ? '' : isCapacitor ? 'mobile-full-width mobile-collapsed-sidebar' : 'full-width collapsed-sidebar'}`}>
      {notification && (
        <Notification
          key={notification.id}
          id={notification.id}
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
        />
      )}

      {selectedReport ? (
        <div className={isCapacitor ? "mobile-report-details" : "report-details"}>
          <div className={isCapacitor ? "mobile-sticky-header" : "sticky-header"}>
            <div className={isCapacitor ? "mobile-buttons-container mobile-single-line-toolbar" : "buttons-container single-line-toolbar"}>
              {/* Conditional rendering based on whether schema is being shown */}
              {hasStructured && actualShowSchema ? (
                <>
                  {/* Schema export buttons when viewing the schema */}
                    <Button
                      icon={FaCopy}
                      variant="info"
                      onClick={handleCopySchema}
                      lowerThreshold={460}
                      upperThreshold={460}
                    >
                      Copier le schéma
                    </Button>
                  
                    <Button
                      icon={FaDownload}
                      variant="word"
                      onClick={handleDownloadSchema}
                      lowerThreshold={480}
                      upperThreshold={480}
                    >
                      Télécharger le schéma
                    </Button>
                </>
              ) : (
                <>
                  {/* Original buttons when viewing text or no structured data */}
                  {(selectedReport.reportType == 'mail' || selectedReport.reportType == 'Mail' || selectedReport.reportType == 'email' || selectedReport.reportType == 'convocation') && (
                <HoverTooltip text="Ouvrir le rapport comme email dans votre logiciel de messagerie" position="bottom">
                  <Button icon={FaEnvelope} variant="lavender" onClick={onSendEmail} lowerThreshold={600} upperThreshold={600}>
                    Envoyer par e-mail
                  </Button>
                </HoverTooltip>
              )}

                  <HoverTooltip text={selectedReport.isCorrected ? "Ce rapport a déjà été utilisé pour amélioré le modèle" : "Utiliser votre correction pour améliorer votre modèle"} position="bottom">
                    <Button
                      icon={isSending ? FaSpinner : FaBrain}
                      collapseOnSmallScreen={isCapacitor ? false : true}
                      variant={!selectedReport.isCorrected ? 'success' : 'secondary'}
                      onClick={onSendToServer}
                      lowerThreshold={460}
                      upperThreshold={460}
                      isSpinning={isSending}
                      style={hasUncertaintyMarkers ? { borderColor: '#ff6b35', borderWidth: '2px' } : {}}
                      title={hasUncertaintyMarkers ? 'Attention: Ce rapport contient des marqueurs d\'incertitude [?]' : ''}
                    >
                      {isCapacitor ? (
                        'Améliorer'
                      ) : (
                        'Améliorer ce modèle'
                      )}
                    </Button>
                  </HoverTooltip>

                  {selectedReport.destinataire && (
                    
                      <Button
                        icon={FaMapMarkerAlt}
                        variant="info"
                        onClick={onToggleDestinatairePanel}
                        lowerThreshold={460}
                        upperThreshold={460}
                      >
                        Destinataire
                      </Button>
                    
                  )}

                  {isCapacitor ? (
                    <HoverTooltip text="Copier le contenu du rapport dans le presse-papiers" position="bottom">
                      <Button icon={FaCopy} variant="info" onClick={onCopy} lowerThreshold={500} upperThreshold={500}>
                        Copier
                      </Button>
                    </HoverTooltip>
                  ) : selectedReport.reportType !== 'mail' && (
                    <HoverTooltip text= {isElectron ? "Insérer le rapport dans votre logiciel" : "" } position="bottom">
                      <Button 
                        icon={FaPaste} 
                        variant="primary" 
                        onClick={onInsertion} 
                        lowerThreshold={520} 
                        upperThreshold={520}
                        style={hasUncertaintyMarkers ? { borderColor: '#ff6b35', borderWidth: '2px' } : {}}
                        title={hasUncertaintyMarkers ? 'Attention: Ce rapport contient des marqueurs d\'incertitude [?]' : ''}
                      >
                       Insérer le rapport
                      </Button>
                    </HoverTooltip>
                  )}
                </>
              )}



                {isElectron ? (
                  <div>
                    {defaultExportDir ? (
                        <MenuButton
                          icon={FaFileWord}
                          variant="word"
                          items={[
                            { key: 'open', label: 'Ouvrir dans le dossier par défaut', icon: FaFileWord, onClick: onExportOpenClick, disabled: !isSilverOrGoldOrEnterprise },
                            { key: 'download', label: 'Télécharger', icon: FaDownload, onClick: onExportDownloadClick },
                            { key: 'sort-date', label: `Trier par date: ${sortMonthDay ? 'Oui' : 'Non'}`, icon: FaFolder, onClick: onToggleSortByDate },
                            { 
                              key: 'folder', 
                              label: `Dossier par défaut: ${shortDefaultExportDir} - modifier`, 
                              icon: FaFolder, 
                              onClick: onSetDefaultFolder
                            },
                          ]}
                          style={hasUncertaintyMarkers ? { borderColor: '#ff6b35', borderWidth: '2px' } : {}}
                          title={hasUncertaintyMarkers ? 'Attention: Ce rapport contient des marqueurs d\'incertitude [?]' : ''}
                        >
                          Exporter en Word
                        </MenuButton>

                    ) : (
                      <MenuButton
                        icon={FaFileWord}
                        variant="word"
                        items={[
                          { key: 'open', label: 'Ouvrir directement', icon: FaFileWord, onClick: onExportOpenClick, disabled: !isSilverOrGoldOrEnterprise },
                          { key: 'download', label: 'Télécharger le fichier', icon: FaDownload, onClick: onExportDownloadClick },
                          { key: 'sort-date', label: `Trier par date: ${sortMonthDay ? 'Oui' : 'Non'}`, icon: FaFolder, onClick: onToggleSortByDate },
                          { 
                            key: 'folder', 
                            label: 'Définir dossier par défaut', 
                            icon: FaFolder, 
                            onClick: onSetDefaultFolder
                          },
                        ]}
                        style={hasUncertaintyMarkers ? { borderColor: '#ff6b35', borderWidth: '2px' } : {}}
                        title={hasUncertaintyMarkers ? 'Attention: Ce rapport contient des marqueurs d\'incertitude [?]' : ''}
                      >
                        Exporter en Word
                      </MenuButton>
                    )}
                  </div>
                ) : !isAndroid ? (
                  <div>
                    <Button 
                      icon={FaFileWord} 
                      variant="word" 
                      onClick={onExportButtonClick} 
                      lowerThreshold={620} 
                      upperThreshold={620}
                      style={hasUncertaintyMarkers ? { borderColor: '#ff6b35', borderWidth: '2px' } : {}}
                      title={hasUncertaintyMarkers ? 'Attention: Ce rapport contient des marqueurs d\'incertitude [?]' : ''}
                    >
                      {isCapacitor ? 'Exporter' : 'Exporter en Word'}
                    </Button>
                  </div>
                ) : (
                 null
                )}

              {/* Prescription button for consultation and suivi reports */}
              {selectedReport.reportType && (selectedReport.reportType.startsWith('consultation') || selectedReport.reportType.startsWith('suivi')) && (
                <HoverTooltip text={!isGoldOrEnterprise ? goldGatingMsg : 'Générer une ordonnance basée sur ce rapport'} position="bottom">
                  <span>
                    <Button 
                      icon={FaPrescriptionBottle} 
                      variant="success" 
                      onClick={onGeneratePrescription}
                      title="Générer une ordonnance"
                      disabled={!isGoldOrEnterprise}
                    >
                      Générer une ordonnance
                    </Button>
                  </span>
                </HoverTooltip>
              )}

              {hasStructured && (

                  <Button 
                    icon={FaChartLine} 
                    variant={actualShowSchema ? 'secondary' : 'orange'} 
                    onClick={() => actualSetShowSchema((v) => !v)}
                    title="Schéma cinétique"
                  >
                    {actualShowSchema ? 'Texte' : 'Schéma cinétique'}
                  </Button>
                
              )}

              

              {/* Hide template button when showing schema */}
              {!(hasStructured && actualShowSchema) && (
                <HoverTooltip text="Génère un nouveau rapport basé sur un autre modèle" position="bottom">
                  <Button icon={FaList} variant="reporttype-consultation" onClick={onToggleReportTypeOverlay} title="Changer le modèle">
                    Changer le modèle
                  </Button>
                </HoverTooltip>
              )}

              {/* Always show ellipsis/undo button */}
                                  <Button icon={FaEllipsisV} variant="print" onClick={onToggleTextOptions}></Button>
            </div>
          </div>

          {/* Insert mode overlay */}
          <SectionsOverlay
            isOpen={isInsertMode}
            onClose={() => onInsertion()}
            sections={sections}
            onCopySection={onCopySection}
            copiedSections={copiedSections}
          />

          {showTextOptions && (
            <div className={isCapacitor ? "mobile-subtoolbar-container" : "subtoolbar-container"}>
              <HoverTooltip text="Exporter les sections du rapport au format CSV dans un tableau" position="bottom">
                <Button icon={FaFileCsv} variant="primary" onClick={onExportSectionsCSV} title="Exporter les sections en CSV">
                  Exporter sections
                </Button>
              </HoverTooltip>
              {selectedReport && (
                  <Button variant="reporttype-consultation" icon={FaCamera} onClick={onToggleAnnexPanel} title="Voir les données extraites">
                    Voir l'annexe
                  </Button>
              )}
              {selectedReport && (
                  <Button variant="secondary" icon={FaEraser} onClick={onCleanFormatting} title="Supprimer le formatage (* _ ** # < >)">
                    Nettoyer *
                  </Button>
              )}

              {isEnterpriseAdmin && (
                <HoverTooltip text="Utiliser ce rapport comme exemple pour un modèle d'organisation" position="bottom">
                  <Button 
                    icon={FaBuilding} 
                    variant="success" 
                    onClick={onCreateEnterpriseExample} 
                    title="Utiliser ce rapport comme exemple d'un rapport organisation"
                    disabled={enterpriseExampleLoading}
                    isSpinning={enterpriseExampleLoading}
                  >
                    Créer un exemple organisation
                  </Button>
                </HoverTooltip>
              )}
              {isSystemAdmin && (
            
                  <Button
                    icon={FaBuilding}
                    variant="orange"
                    onClick={onCreateAdminDefaultExample}
                    title="Créer un exemple par défaut (admin) pour ce type"
                    disabled={adminDefaultExampleLoading}
                    isSpinning={adminDefaultExampleLoading}
                  >
                    Créer un exemple admin
                  </Button>
                
              )}
            </div>
          )}

          {showExportMenu && (
            <div className={isCapacitor ? "mobile-subtoolbar-container" : "subtoolbar-container"}>
              <HoverTooltip text={"Exporter le rapport au format Word (.docx)"} position="bottom" arrow={true} >
                <div>
                  <Button type="fixed" icon={FaFileWord} variant="word" onClick={onExportButtonClick} >
                    Exporter en Word
                  </Button>
                </div>
              </HoverTooltip>
              <HoverTooltip text="Imprimer le rapport" position="bottom">
                <Button type="fixed" icon={FaPrint} variant="word" onClick={onPrint}>
                  Imprimer
                </Button>
              </HoverTooltip>
            </div>
          )}
          
          <div className={isCapacitor ? "mobile-editor-scrollable" : "editor-scrollable"}>
  <div className="editor-wrapper">
    {actualShowSchema && hasStructured ? (
      <StructuredDataManager
        ref={structuredDataRef}
        structuredData={selectedReport.structuredData}
        onDataChange={onBullseyeChange}
        reportType={selectedReport.reportType}
        reportText={mdReport || ''}
        showButton={false}
        inlineMode={true}
        textareaRef={textareaRef}
      />
    ) : (
      <textarea
        ref={textareaRef}
        className={isCapacitor ? 'mobile-simple-text-editor' : 'simple-text-editor'}
        value={mdReport}
        onChange={e => {
          if (!isFullScreenEnabled || !isCapacitor) {
            onMdReportChange(e.target.value);
            onUpdateCaretPosition();
            autoResize();
          }
        }}
        onClick={isFullScreenEnabled && isCapacitor ? handleTextareaClick : onUpdateCaretPosition}
        onKeyUp={onUpdateCaretPosition}
        onFocus={isFullScreenEnabled && isCapacitor ? handleTextareaFocus : onUpdateCaretPosition}
        placeholder="Commencez à rédiger votre rapport médical..."
        spellCheck="true"
        readOnly={isFullScreenEnabled && isCapacitor}
      />
    )}
  </div>
</div>

{/* Uncertainty Warning Banner */}
<div style={{
  position: 'fixed',
  bottom: isCapacitor ? `15px` : `5px`,
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: hasUncertaintyMarkers ? '#fff3cd' : '#fffbf0',
  border: hasUncertaintyMarkers ? '2px solid #ff6b35' : '1px solid #ffeaa7',
  borderRadius: '8px',
  padding: hasUncertaintyMarkers ? '12px 20px' : '6px 12px',
  boxShadow: hasUncertaintyMarkers ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  alignItems: 'center', // Centre vertical
  justifyContent: 'center', // Centre horizontal
  gap: hasUncertaintyMarkers ? '10px' : '6px',
  color: hasUncertaintyMarkers ? '#856404' : '#856404',
  zIndex: 1000,
  maxWidth: hasUncertaintyMarkers ? '700px' : '300px',
  minWidth: hasUncertaintyMarkers ? '300px' : '200px',
  width: hasUncertaintyMarkers ? 'auto' : 'fit-content',
  backdropFilter: 'blur(8px)',
  animation: 'slideUp 0.3s ease-out',
  opacity: 1,
  transition: 'all 0.3s ease',
  whiteSpace: hasUncertaintyMarkers ? 'normal' : 'nowrap',
  maxHeight: hasUncertaintyMarkers ? '120px' : 'auto',
  overflowY: hasUncertaintyMarkers ? 'auto' : 'hidden',
  overflowX: 'hidden',
  position: 'relative',
  textAlign: 'center' // Centre le texte
}}>
  {hasUncertaintyMarkers ? (
    <>
      <FaExclamationTriangle style={{ 
        color: '#ff6b35', 
        flexShrink: 0, 
        fontSize: '18px'
      }} />
      <span style={{ 
        fontSize: '14px', 
        fontWeight: '500', 
        lineHeight: '1.4',
        wordBreak: 'break-word',
        textAlign: 'center' // Centre le texte dans le span
      }}>
        {uncertaintyPreview ? (
          <>
            Ce rapport contient des marqueurs d'incertitude: <strong>{uncertaintyPreview}</strong>. Veuillez vérifier le contenu.
          </>
        ) : (
          <>
            Ce rapport contient des marqueurs d'incertitude <strong>[?]</strong>. Veuillez vérifier le contenu.
          </>
        )}
      </span>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button 
          icon={FaEraser}
          variant="warning"
          onClick={onCleanUncertaintyMarkers}
          style={{ marginLeft: '8px', whiteSpace: 'nowrap' }}
        >
          {!isCapacitor ? "Nettoyer les marqueurs" : null}
        </Button>
      </div>
      
      {/* Gradient overlay si le texte est long */}
      {uncertaintyPreview && uncertaintyPreview.length > 100 && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '30px',
          background: 'linear-gradient(to bottom, rgba(255, 243, 205, 0), rgba(255, 243, 205, 1))',
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
          pointerEvents: 'none'
        }} />
      )}
    </>
  ) : (
    <>
      <span style={{ 
        fontSize: '11px', 
        fontWeight: '400', 
        lineHeight: '1.2',
        textAlign: 'center',
        width: '100%'
      }}>
        voxia peut faire des erreurs, vérifiez toujours le contenu
      </span>
    </>
  )}
</div>
</div>
) : (
<div className={isCapacitor ? "mobile-report-details-placeholder" : "report-details-placeholder"}>
  
</div>
)}

{!isSidebarVisible && selectedReport && (
<div
  className={isCapacitor ? "mobile-collapsed-sidebar-indicator" : "collapsed-sidebar-indicator"}
  onMouseEnter={() => isSmallScreen && !isCapacitor && onSetSidebarVisible(true)}
  onClick={onSidebarIndicatorClick}
>
  <FaChevronRight className={isCapacitor ? "mobile-arrow-icon" : "arrow-icon"} />
</div>
)}
</div>
);
}